/* eslint-disable no-fallthrough */
import { Location } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseTableActionEvent } from '@app/component/entity-table/base-table/base-table-action-event';
import { SearchField } from '@core/base/objects/search/search-field';
import { SearchOperator } from '@core/base/objects/search/search-operator.enum';
import { Permissions } from '@core/constants/permissions';
import { Pagination } from '@core/objects/pagination';
import { SortOrder } from '@core/objects/sort-order';
import { NotificationService } from '@core/services/notification.service';
import { SessionService } from '@core/services/session.service';
import { RestSearchUtil } from '@core/utils/rest-search-util';
import { environment } from '@environments/environment';
import { Asset } from '@shared/asset/asset';
import { AssetApi } from '@shared/asset/asset-api.service';
import { Beneficiary } from '@shared/beneficiary/beneficiary';
import { BeneficiaryApi } from '@shared/beneficiary/beneficiary-api.service';
import { BeneficiaryGroup } from '@shared/beneficiary/beneficiary-group';
import { BeneficiaryGroupApi } from '@shared/beneficiary/beneficiary-group-api.service';
import { BeneficiaryGroupHelper } from '@shared/beneficiary/beneficiary-group-helper.service';
import {
  BeneficiaryGroupStatus,
  beneficiaryGroupStatusGetRoleForStatusUpdate,
  beneficiaryGroupStatusNextStatus,
} from '@shared/beneficiary/beneficiary-group-status.enum';
import { BeneficiaryHelper } from '@shared/beneficiary/beneficiary-helper.service';
import { ChangeRequest } from '@shared/change-request/change-request';
import { ChangeRequestApi } from '@shared/change-request/change-request-api.service';
import { ChangeRequestHelperService } from '@shared/change-request/change-request-helper.service';
import { Comment } from '@shared/comment/comment';
import { Country } from '@shared/country/country';
import { Enum } from '@shared/enum/enum';
import { Coach } from '@shared/local-partner/coach/coach';
import { CoachApi } from '@shared/local-partner/coach/coach-api.service';
import { LocalPartner } from '@shared/local-partner/local-partner';
import { LocalPartnerApi } from '@shared/local-partner/local-partner-api.service';
import { FieldAffiliate } from '@shared/organisation/field-affiliate/field-affiliate';
import { FieldAffiliateApiService } from '@shared/organisation/field-affiliate/field-affiliate-api.service';
import { OrganisationApiService } from '@shared/organisation/organisation-api.service';
import { OrganisationProject } from '@shared/organisation/project/organisation-project';
import { Quest } from '@shared/quest/quest';
import { DatetimeUtil } from '@shared/util/datetime.util';
import { WorkflowTask } from '@shared/workflow/task/workflow-task';
import { WorkflowTaskApi } from '@shared/workflow/task/workflow-task-api.service';
import { WatchProfile } from '@shared/workflow/watch-profile/watch-profile';
import { BehaviorSubject, Observable, of as observableOf, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-beneficiary-group-edit',
  templateUrl: './beneficiary-group-edit.component.html',
  styleUrls: ['./beneficiary-group-edit.component.scss'],
  standalone: false,
})
export class BeneficiaryGroupEditComponent implements OnInit, OnDestroy {
  static readonly UPDATED_GROUP_UUID_PLACEHOLDER = '<UPDATED_GROUP_UUID>';

  beneficiaryGroupHelper = BeneficiaryGroupHelper;
  rolesToEdit = [Permissions.ROLE_LEVEL_40];
  roleToEditCoach = this.rolesToEdit;
  roleToEditPaymentData = this.rolesToEdit;
  roleToSeeCostCenter = [Permissions.ROLE_LEVEL_60];

  private requiredFields: string[] = [];
  private lockedFields: string[] = [];

  assets: Asset[] = [];
  beneficiaryGroup!: BeneficiaryGroup;
  organisationProject?: OrganisationProject;
  editForm!: UntypedFormGroup;
  isNew = false;
  showNextStatusDate = false;
  nextStatusDatePickerStartDate: Date = new Date();
  nextStatusRunningTime: number | null = null;
  showDeleteBeneficiaries = false;
  nextStatus: BeneficiaryGroupStatus | null = null;
  countryList!: Country[];
  watchProfileList!: WatchProfile[];
  organisationProjectList!: OrganisationProject[];
  countryOrganisationProjectList = new BehaviorSubject<OrganisationProject[]>([]);
  localPartnerList: EventEmitter<LocalPartner[]> = new EventEmitter<LocalPartner[]>();
  coachList: EventEmitter<Coach[]> = new EventEmitter<Coach[]>();
  private allFieldAffiliateList = new Map<string, FieldAffiliate[]>();
  fieldAffiliateList: FieldAffiliate[] = [];

  // Embedded table values
  displayedBeneficiaryColumns = ['image', 'firstName', 'name', 'code', 'dateOfBirth', 'status'];
  beneficiaryDataSource!: Beneficiary[];
  displayedTaskColumns = ['title', 'performer', 'dueDate', 'doneOn', 'questionnaireForm', 'questRequired', 'done'];
  commentDataSource!: Observable<Comment[]>;
  taskDataSource!: Observable<WorkflowTask[]>;
  taskCount?: number;
  changeRequestDataSource!: ChangeRequest[];
  openChangeRequestCount?: number;

  // IDirtyComponent variables
  isDirtySubject = new BehaviorSubject(false);
  isDirty$: Observable<boolean> = this.isDirtySubject.asObservable();
  valueChangedSubscription!: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private assetApi: AssetApi,
    private beneficiaryGroupApi: BeneficiaryGroupApi,
    private changeRequestApi: ChangeRequestApi,
    private changeRequestHelperService: ChangeRequestHelperService,
    private fieldAffiliateApi: FieldAffiliateApiService,
    private localPartnerApi: LocalPartnerApi,
    private organisationApi: OrganisationApiService,
    private coachApi: CoachApi,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private sessionService: SessionService,
    private beneficiaryApi: BeneficiaryApi,
    private notificationService: NotificationService,
    private workflowTaskApi: WorkflowTaskApi,
  ) {
    this.createForm();
    if (!this.sessionService.hasRole(this.rolesToEdit)) {
      this.editForm.disable();
    }
  }

  static groupCodePattern(control: AbstractControl): ValidationErrors | null {
    const codeValue = control.value as string;
    if (!codeValue) {
      return null;
    }

    if (!control.parent || !control.parent.get('country')?.value) {
      if (codeValue.match('^[A-Z]{3}\\d{3}$')) {
        return null;
      } else {
        return {
          pattern: 'Group code should be formatted as "XXX023"',
        } as ValidationErrors;
      }
    } else {
      const selectedCountry = control.parent.get('country')?.value as Country;
      if (codeValue.match(`^${selectedCountry.iso3166alpha3}\\d{3}$`)) {
        return null;
      } else {
        return {
          pattern: `Group code should be formatted as "${selectedCountry.iso3166alpha3}023"`,
        } as ValidationErrors;
      }
    }
  }

  ngOnInit(): void {
    this.beneficiaryGroup = this.route.snapshot.data.beneficiaryGroup;
    this.watchProfileList = this.route.snapshot.data.watchProfileList;
    this.organisationProjectList = this.route.snapshot.data.organisationProjectList;
    this.commentDataSource = this.route.snapshot.data.commentList;
    this.isNew = !this.beneficiaryGroup.uuid;
    this.showDeleteBeneficiaries = !this.isNew && !environment.production;

    if (this.isNew) {
      this.organisationProject = this.route.snapshot.data.organisationProject;
      if (this.organisationProject) {
        this.organisationProjectList = [this.organisationProject];
        this.beneficiaryGroup.organisationProject = this.organisationProject;
        this.beneficiaryGroup.amount = this.organisationProject.beneficiaryPaymentAmount;
        this.beneficiaryGroup.watchProfile = this.organisationProject.watchProfile;
        this.beneficiaryGroup.maxRunningTime = this.organisationProject.programRunTime;
        if (this.organisationProject.watchProfile) {
          this.watchProfileList = [this.organisationProject.watchProfile];
          this.beneficiaryGroup.watchProfile = this.organisationProject.watchProfile;
        } else {
          this.watchProfileList = [];
        }
      }
      if (this.organisationProject?.country) {
        this.beneficiaryGroup.country = this.organisationProject.country;
        this.editForm.get('code')?.setValue(this.beneficiaryGroup.country.iso3166alpha3?.toUpperCase());
      } else {
        this.countryList = this.route.snapshot.data.countryList;
      }
    } else {
      this.changeRequestDataSource = this.route.snapshot.data.changeRequestList;
      this.openChangeRequestCount = this.changeRequestDataSource.filter(cr => cr.status === 'REQUESTED').length;
    }

    this.requiredFields = this.getRequiredFields(this.beneficiaryGroup.status?.code as BeneficiaryGroupStatus);
    this.requiredFields.forEach(fieldName => {
      this.editForm.get(fieldName)?.setValidators([Validators.required]);
    });

    this.lockedFields = this.getLockedFields(this.beneficiaryGroup.status?.code as BeneficiaryGroupStatus);
    this.lockedFields.forEach(fieldName => {
      this.editForm.get(fieldName)?.disable();
    });

    if (this.isLocked('localPartner')) {
      this.localPartnerList.next([this.beneficiaryGroup.localPartner]);
    } else if (this.beneficiaryGroup.country) {
      this.localPartnerApi
        .findByCountry(this.beneficiaryGroup.country.uuid)
        .subscribe(result => this.localPartnerList.next(result));
    }

    if (!this.isNew) {
      if (this.isLocked('organisationProject') && this.beneficiaryGroup.organisationProject) {
        this.countryOrganisationProjectList.next([this.beneficiaryGroup.organisationProject]);
      } else if (this.beneficiaryGroup.country) {
        const filteredProjects = this.organisationProjectList.filter(
          project => project.country && project.country.code === this.beneficiaryGroup.country.code,
        );
        this.countryOrganisationProjectList.next(filteredProjects);
      }
    }

    this.determineAvailableFieldAffiliateList(this.beneficiaryGroup.organisationProject);

    const nextStatusTemp = beneficiaryGroupStatusNextStatus(this.beneficiaryGroup.status?.code);
    const roleForStatusUpdate = beneficiaryGroupStatusGetRoleForStatusUpdate(nextStatusTemp);
    if (roleForStatusUpdate && this.sessionService.hasRole(roleForStatusUpdate)) {
      this.nextStatus = nextStatusTemp;
    }

    this.showNextStatusDate =
      this.nextStatus === BeneficiaryGroupStatus.IN_PROGRAM ||
      this.nextStatus === BeneficiaryGroupStatus.OUT_OF_PROGRAM;
    if (this.showNextStatusDate) {
      let nextStatusDate = null;

      switch (this.nextStatus) {
        case BeneficiaryGroupStatus.IN_PROGRAM:
          nextStatusDate = this.beneficiaryGroup.startDate;
          break;
        case BeneficiaryGroupStatus.OUT_OF_PROGRAM: {
          nextStatusDate = this.beneficiaryGroup.endDate;
          this.nextStatusDatePickerStartDate = BeneficiaryGroupHelper.calculateExpectedEndDate(this.beneficiaryGroup);
          this.nextStatusRunningTime = DatetimeUtil.calculateWeeksBetweenDates(
            this.beneficiaryGroup.startDate,
            this.beneficiaryGroup.endDate,
          );
          break;
        }
      }
      if (nextStatusDate) {
        this.nextStatusDatePickerStartDate = nextStatusDate;
      }

      this.editForm.patchValue({ nextStatusDate });
    } else {
      this.editForm.removeControl('nextStatusDate');
    }

    this.editForm.patchValue(this.beneficiaryGroup);
    this.editForm.updateValueAndValidity();

    if (!this.isNew) {
      this.createBeneficiaryDataSource('code');
      this.createTaskDataSource('dueDate');
    }

    this.valueChangedSubscription = this.editForm.valueChanges.subscribe(() => {
      this.isDirtySubject.next(this.editForm.dirty);
    });

    this.loadAssets();
  }

  ngOnDestroy(): void {
    if (this.valueChangedSubscription) {
      this.valueChangedSubscription.unsubscribe();
    }
  }

  private createForm(): void {
    // Required validator and disabled are managed after createForm is called.
    this.editForm = this.formBuilder.group({
      uuid: null,
      code: [
        null,
        {
          validators: [BeneficiaryGroupEditComponent.groupCodePattern, Validators.required],
          asyncValidators: [this.uniqueCodeValidator.bind(this)],
        },
      ],
      country: null,
      province: null,
      parish: null,
      watchProfile: null,
      organisationProject: null,
      fieldAffiliate: null,
      coach: null,
      onboardingGroupSize: 0,
      localPartner: null,
      localName: [null, { validators: [Validators.maxLength(100)] }],
      status: { value: BeneficiaryGroupStatus.FORMING, disabled: true },
      amount: null,
      maxRunningTime: null,
      selectedDate: { value: null, disabled: true },
      onboardedDate: { value: null, disabled: true },
      startDate: { value: null, disabled: false },
      endDate: { value: null, disabled: true },
      nextStatusDate: null,
      costCenter: this.formBuilder.group({
        code: null,
      }),
    });
  }

  back(beneficiaryGroupUuid?: string | null): void {
    const callbackUrl = this.route.snapshot.queryParamMap.get('callbackUrl');
    if (callbackUrl && beneficiaryGroupUuid) {
      const goToUrl = callbackUrl.replace(
        BeneficiaryGroupEditComponent.UPDATED_GROUP_UUID_PLACEHOLDER,
        beneficiaryGroupUuid,
      );
      this.router.navigateByUrl(goToUrl);
    } else {
      this.location.back();
    }
  }

  save(): void {
    this.updateBeneficiaryGroupBeforeSave();
    if (this.beneficiaryGroup.uuid) {
      this.beneficiaryGroupApi.update(this.beneficiaryGroup).subscribe(() => {
        this.isDirtySubject.next(false);
        this.back();
      });
    } else {
      this.beneficiaryGroupApi.save(this.beneficiaryGroup).subscribe(value => {
        let newGroupUuid: string | null = null;

        if (value) {
          if (value instanceof URL) {
            newGroupUuid = value.pathname.substring(value.pathname.lastIndexOf('/') + 1);
          } else {
            newGroupUuid = value.uuid;
          }
          this.location.replaceState(
            this.route.snapshot.pathFromRoot
              .map(a => a.url)
              .reduce((accumulater, urlArray) => accumulater.concat(urlArray), [])
              .map(url => url.path)
              .map(path => (path === 'new' ? newGroupUuid : path))
              .join('/'),
          );
        }

        this.isDirtySubject.next(false);
        this.back(newGroupUuid);
      });
    }
  }

  updateToNextStatus(): void {
    if (!this.canUpdateToNextStatus()) {
      return;
    }

    if (this.nextStatus) {
      this.updateBeneficiaryGroupBeforeSave();

      if (this.nextStatus === BeneficiaryGroupStatus.IN_PROGRAM && !this.beneficiaryGroup.startDate) {
        this.notificationService.error('Please set a start date before moving the group to IN PROGRAM', 'Error');

        return;
      }
      if (this.nextStatus === BeneficiaryGroupStatus.OUT_OF_PROGRAM && !this.beneficiaryGroup.endDate) {
        this.notificationService.error('Please set an end date before moving the group to OUT OF PROGRAM', 'Error');

        return;
      }

      this.beneficiaryGroupApi.updateStatus(this.beneficiaryGroup, this.nextStatus).subscribe(() => {
        this.isDirtySubject.next(false);
        this.back(); // TODO Reload?
      });
    }
  }

  updateBeneficiaryGroupBeforeSave(): void {
    const lockedFields = this.getLockedFields(this.beneficiaryGroup.status?.code as BeneficiaryGroupStatus);
    this.beneficiaryGroup.localName = this.editForm.get('localName')?.value;

    if (lockedFields.indexOf('country') < 0) {
      this.beneficiaryGroup.country = this.editForm.get('country')?.value;
    }
    if (lockedFields.indexOf('code') < 0) {
      this.beneficiaryGroup.code = this.editForm.get('code')?.value;
    }
    if (lockedFields.indexOf('watchProfile') < 0) {
      this.beneficiaryGroup.watchProfile = this.editForm.get('watchProfile')?.value;
    }
    if (lockedFields.indexOf('organisationProject') < 0) {
      this.beneficiaryGroup.organisationProject = this.editForm.get('organisationProject')?.value;
    }
    if (lockedFields.indexOf('fieldAffiliate') < 0) {
      this.beneficiaryGroup.fieldAffiliate = this.editForm.get('fieldAffiliate')?.value;
    }
    if (lockedFields.indexOf('onboardingGroupSize') < 0) {
      this.beneficiaryGroup.onboardingGroupSize = this.editForm.get('onboardingGroupSize')?.value;
    }
    if (lockedFields.indexOf('maxRunningTime') < 0) {
      this.beneficiaryGroup.maxRunningTime = this.editForm.get('maxRunningTime')?.value;
    }
    if (lockedFields.indexOf('coach') < 0) {
      this.beneficiaryGroup.coach = this.editForm.get('coach')?.value;
    }
    if (lockedFields.indexOf('province') < 0) {
      this.beneficiaryGroup.province = this.editForm.get('province')?.value;
    }
    if (lockedFields.indexOf('parish') < 0) {
      this.beneficiaryGroup.parish = this.editForm.get('parish')?.value;
    }

    if (lockedFields.indexOf('localPartner') < 0) {
      this.beneficiaryGroup.localPartner = this.editForm.get('localPartner')?.value;
    }
    if (lockedFields.indexOf('amount') < 0) {
      this.beneficiaryGroup.amount = this.editForm.get('amount')?.value;
    }
    if (lockedFields.indexOf('startDate') < 0) {
      this.beneficiaryGroup.startDate = this.editForm.get('startDate')?.value;
    }

    if (this.showNextStatusDate) {
      switch (this.nextStatus) {
        case BeneficiaryGroupStatus.IN_PROGRAM:
          this.beneficiaryGroup.startDate = this.editForm.get('nextStatusDate')?.value;
          break;
        case BeneficiaryGroupStatus.OUT_OF_PROGRAM:
          this.beneficiaryGroup.endDate = this.editForm.get('nextStatusDate')?.value;
          break;
      }
    }
  }

  hasNextStatus(): boolean {
    return this.nextStatus !== null;
  }

  canUpdateToNextStatus(): boolean {
    if (!this.hasNextStatus()) {
      return false;
    }

    return this.getRequiredFields(this.nextStatus).every(fieldName => this.editForm.get(fieldName)?.value);
  }

  updateNextStatusRunningTime(): void {
    if (this.nextStatus === BeneficiaryGroupStatus.OUT_OF_PROGRAM) {
      this.nextStatusRunningTime = DatetimeUtil.calculateWeeksBetweenDates(
        this.beneficiaryGroup.startDate,
        this.editForm.get('nextStatusDate')?.value,
      );
    }
  }

  countryUpdated(selectedCountry: Country): void {
    if (selectedCountry) {
      this.editForm.get('code')?.setValue(selectedCountry.iso3166alpha3?.toUpperCase());

      const filteredProjects = this.organisationProjectList.filter(
        project => project.country && project.country.code === selectedCountry.code,
      );
      this.countryOrganisationProjectList.next(filteredProjects);
      const preselectedProject = filteredProjects.find(project => project.o100WeeksProject);
      if (preselectedProject) {
        this.editForm.get('organisationProject')?.setValue(preselectedProject);
      }

      this.localPartnerApi.findByCountry(selectedCountry.uuid).subscribe(result => {
        this.localPartnerList.next(result);
        this.editForm.get('localPartner')?.reset();
      });
    } else {
      this.countryOrganisationProjectList.next([]);
      if (this.editForm.get('organisationProject')?.value) {
        this.editForm.get('organisationProject')?.reset();
      }

      this.localPartnerList.next([]);
      if (this.editForm.get('localPartner')?.value) {
        this.editForm.get('localPartner')?.reset();
      }
    }
  }

  localPartnerUpdated(selectedLocalPartner: LocalPartner): void {
    if (this.isLocked('coach')) {
      this.coachList.next(this.beneficiaryGroup.coach ? [this.beneficiaryGroup.coach] : []);
    } else if (selectedLocalPartner && selectedLocalPartner.uuid) {
      this.coachApi.findByLocalPartner(selectedLocalPartner.uuid).subscribe(result => this.coachList.next(result));
    } else {
      this.coachList.next([]);
    }
  }

  projectUpdated(organisationProject?: OrganisationProject): void {
    if (organisationProject) {
      this.beneficiaryGroup.amount = organisationProject.beneficiaryPaymentAmount;
      this.beneficiaryGroup.watchProfile = organisationProject.watchProfile;
      this.beneficiaryGroup.maxRunningTime = organisationProject.programRunTime;
    }
    this.determineAvailableFieldAffiliateList(organisationProject);
  }

  private createBeneficiaryDataSource(sort: string): void {
    const filter = RestSearchUtil.createSearchFieldGroup([
      {
        fieldName: 'group.uuid',
        operator: SearchOperator.EQ,
        value: this.beneficiaryGroup.uuid,
      } as SearchField,
    ]);
    const pagination = new Pagination(0, 100, new SortOrder(sort));

    this.beneficiaryApi
      .findByRequest(filter, pagination)
      .pipe(map(result => result.content))
      .subscribe(result => (this.beneficiaryDataSource = result));
  }

  private createTaskDataSource(sort: string): void {
    const filter = RestSearchUtil.createSearchFieldGroup([
      {
        fieldName: 'group.uuid',
        operator: SearchOperator.EQ,
        value: this.beneficiaryGroup.uuid,
      } as SearchField,
    ]);
    const pagination = new Pagination(0, 100, new SortOrder(sort));

    this.taskDataSource = this.workflowTaskApi.findByRequest(filter, pagination).pipe(
      tap(result => (this.taskCount = result.totalElements)),
      map(result => result.content),
    );
    this.taskDataSource.subscribe();
  }

  sortTasks($event: Sort): void {
    this.createTaskDataSource(($event.direction === 'desc' ? '-' : '') + $event.active);
  }

  getRequiredFields(status: BeneficiaryGroupStatus | null): string[] {
    const requiredFields = [];
    requiredFields.push('country');
    requiredFields.push('organisationProject');

    if (status === null) {
      return requiredFields;
    }

    switch (status) {
      case BeneficiaryGroupStatus.OUT_OF_PROGRAM:
      // tslint:disable-next-line:no-switch-case-fall-through
      case BeneficiaryGroupStatus.IN_PROGRAM:
      // tslint:disable-next-line:no-switch-case-fall-through
      case BeneficiaryGroupStatus.ONBOARDING_COMPLETED:
        requiredFields.push('amount');
        requiredFields.push('maxRunningTime');
      // tslint:disable-next-line:no-switch-case-fall-through
      case BeneficiaryGroupStatus.ONBOARDED_IN_FIELD:
      // tslint:disable-next-line:no-switch-case-fall-through
      case BeneficiaryGroupStatus.ONBOARDING:
      // tslint:disable-next-line:no-switch-case-fall-through
      case BeneficiaryGroupStatus.SURVEYING:
      // SPECIAL: No beneficiary can have the status CREATED
      // tslint:disable-next-line:no-switch-case-fall-through
      case BeneficiaryGroupStatus.FORMING:
    }

    return requiredFields;
  }

  getLockedFields(status: BeneficiaryGroupStatus): string[] {
    const lockedFields = [];

    if (status) {
      lockedFields.push('code');
      lockedFields.push('country');

      switch (status) {
        case BeneficiaryGroupStatus.OUT_OF_PROGRAM:
          lockedFields.push('coach');
          lockedFields.push('maxRunningTime');
          lockedFields.push('fieldAffiliate');
        // tslint:disable-next-line:no-switch-case-fall-through
        case BeneficiaryGroupStatus.IN_PROGRAM:
          lockedFields.push('startDate');
          lockedFields.push('amount');
        // tslint:disable-next-line:no-switch-case-fall-through
        case BeneficiaryGroupStatus.ONBOARDING_COMPLETED:
          lockedFields.push('province');
          lockedFields.push('parish');
          lockedFields.push('onboardingGroupSize');
        // tslint:disable-next-line:no-switch-case-fall-through
        case BeneficiaryGroupStatus.ONBOARDED_IN_FIELD:
        // tslint:disable-next-line:no-switch-case-fall-through
        case BeneficiaryGroupStatus.ONBOARDING:
        // tslint:disable-next-line:no-switch-case-fall-through
        case BeneficiaryGroupStatus.SURVEYING:
        // tslint:disable-next-line:no-switch-case-fall-through
        case BeneficiaryGroupStatus.FORMING:
      }
    }

    if (!this.sessionService.hasRole(this.roleToEditCoach)) {
      lockedFields.push('coach');
    }

    if (!this.canEditProject()) {
      lockedFields.push('organisationProject');
    }

    if (this.beneficiaryGroup.organisationProject) {
      lockedFields.push('amount');
      lockedFields.push('maxRunningTime');
      lockedFields.push('watchProfile');
    }

    const latestQuest = this.getLatestQuest();
    if (latestQuest?.localPartner) {
      lockedFields.push('localPartner');
    }
    if (latestQuest?.fieldAffiliate) {
      lockedFields.push('fieldAffiliate');
    }

    return lockedFields;
  }

  isRequired(fieldName: string): boolean {
    return this.requiredFields.indexOf(fieldName) >= 0;
  }

  isLocked(fieldName: string): boolean {
    return this.lockedFields.indexOf(fieldName) >= 0;
  }

  upload(files: File[]): void {
    for (const file of files) {
      if (this.beneficiaryGroup.uuid) {
        this.beneficiaryGroupApi.upload(this.beneficiaryGroup.uuid, file, Asset.DOCUMENT_TYPE_PHOTO).subscribe(
          assetUuid => {
            if (assetUuid) {
              this.assetApi.get(assetUuid).subscribe(asset => {
                if (asset.uuid) {
                  this.assetApi.getImage(asset.uuid).subscribe(image => {
                    asset.imageUri = image;
                    if (this.assets) {
                      this.assets.push(asset);
                    }
                  });
                }
              });
            }
          },
          error => {
            console.error(`[BeneficiaryGroupEdit] error saving asset: `, error.error);
          },
        );
      }
    }
  }

  deleteAsset(asset: Asset): void {
    if (asset && asset.uuid) {
      this.beneficiaryGroupApi.removeAsset(this.beneficiaryGroup, asset).subscribe(() => {
        if (this.assets) {
          this.assets.forEach((item, index) => {
            if (item.uuid === asset.uuid) {
              this.assets?.splice(index, 1);
            }
          });
        }
      });
    }
  }

  changeAsset(asset: Asset, newDocumentType: Enum): void {
    if (asset?.uuid) {
      this.assetApi.setDocumentType(asset.uuid, newDocumentType.code).subscribe(success => {
        if (success) {
          this.loadAssets();
        } else {
          this.notificationService.error('Failed to change the document type of the asset.', 'Error');
        }
      });
    }
  }

  loadAssets(): void {
    if (this.beneficiaryGroup.uuid) {
      this.beneficiaryGroupApi.assets(this.beneficiaryGroup.uuid).subscribe(value => {
        if (value) {
          this.assets = value;
          value.forEach(asset => {
            if (asset.uuid) {
              this.assetApi.getImage(asset.uuid).subscribe(value1 => {
                asset.imageUri = value1;
              });
            }
          });
        } else {
          this.assets = [];
        }
      });
    }
  }

  uniqueCodeValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    const groupCode = control.value;
    if (!groupCode) {
      return observableOf(null);
    }

    const uuid = this.beneficiaryGroup.uuid;

    return this.beneficiaryGroupApi.codeUnique(uuid ? uuid : '', groupCode).pipe(
      map(valid => {
        if (valid) {
          return null;
        } else {
          return {
            codeNotUnique: 'This code is already in use.',
          };
        }
      }),
    );
  }

  private canEditProject(): boolean {
    if (this.isNew) {
      // Only allow to edit the project when we are not creating the group for a specific project.
      return !this.organisationProject;
    }

    if (this.beneficiaryGroup.questList?.length) {
      // Cannot edit the project, since the quest linked to the group is part of the project.
      return false;
    }

    if (!this.beneficiaryGroup.organisationProject) {
      return true;
    }

    if (this.beneficiaryGroup.readyForDonationsSequence === 0) {
      // Don't update the project when we're already using the moneybar goal for this group
      return false;
    }

    if (!this.sessionService.hasRole([Permissions.ROLE_LEVEL_60])) {
      // Only the Manager can update the Project
      return false;
    }

    return true;
  }

  sortBeneficiaries($event: Sort): void {
    if (!this.beneficiaryDataSource) {
      return;
    }

    const data = this.beneficiaryDataSource.slice();

    let sortFn: ((a: Beneficiary, b: Beneficiary) => number) | undefined;
    switch ($event.active) {
      case 'code':
        sortFn = BeneficiaryHelper.sortByCode;
        break;
      case 'firstName':
        sortFn = BeneficiaryHelper.sortByContactFirstName;
        break;
      case 'name':
        sortFn = BeneficiaryHelper.sortByContactLastName;
        break;
      case 'dateOfBirth':
        sortFn = BeneficiaryHelper.sortByContactDateOfBirth;
        break;
      case 'status':
        sortFn = BeneficiaryHelper.sortByStatus;
        break;
    }

    if (sortFn !== undefined) {
      data.sort(sortFn);
      if ($event.direction === 'desc') {
        data.reverse();
      }
    }

    this.beneficiaryDataSource = data;
  }

  deleteBeneficiaries(): void {
    this.beneficiaryGroupApi.deleteBeneficiaries(this.beneficiaryGroup.uuid).subscribe({
      next: () => this.notificationService.success('Beneficiaries deleted successfully', 'Success'),
    });
  }

  private determineAvailableFieldAffiliateList(forProject?: OrganisationProject): void {
    let loadingAffiliates = false;

    const latestQuest = this.getLatestQuest();
    if (latestQuest?.fieldAffiliate) {
      this.fieldAffiliateList = [latestQuest.fieldAffiliate];
    } else {
      const organisation = forProject?.contract?.organisation;
      if (organisation) {
        if (organisation.fieldAffiliates !== null && organisation.fieldAffiliates !== undefined) {
          this.fieldAffiliateList = organisation.fieldAffiliates;
        } else {
          const fieldAffiliateCacheKey = organisation.uuid;
          const cachedFieldAffiliateList = this.allFieldAffiliateList.get(fieldAffiliateCacheKey);
          if (cachedFieldAffiliateList) {
            this.fieldAffiliateList = cachedFieldAffiliateList;
          } else {
            loadingAffiliates = true;
            this.fieldAffiliateList = [];
            this.organisationApi.findByUuid(fieldAffiliateCacheKey).subscribe(result => {
              this.allFieldAffiliateList.set(fieldAffiliateCacheKey, result.fieldAffiliates);
              this.fieldAffiliateList = result.fieldAffiliates;
              this.validateFieldAffiliate();
            });
          }
        }
      } else {
        const cachedFieldAffiliateList = this.allFieldAffiliateList.get('');
        if (cachedFieldAffiliateList) {
          this.fieldAffiliateList = cachedFieldAffiliateList;
        } else {
          loadingAffiliates = true;
          this.fieldAffiliateList = [];
          this.fieldAffiliateApi.findAll().subscribe(result => {
            this.allFieldAffiliateList.set('', result);
            this.fieldAffiliateList = result;
            this.validateFieldAffiliate();
          });
        }
      }
    }

    if (!loadingAffiliates) {
      this.validateFieldAffiliate();
    }
  }

  private validateFieldAffiliate(): void {
    const currentSelectedValue = this.editForm.get('fieldAffiliate')?.value;
    if (currentSelectedValue && !this.fieldAffiliateList.map(fa => fa.uuid).includes(currentSelectedValue.uuid)) {
      this.editForm.get('fieldAffiliate')?.reset();
    }
  }

  handleChangeRequestAction(action: BaseTableActionEvent<ChangeRequest>): void {
    this.changeRequestHelperService.handleChangeRequestAction(action).subscribe(actionResult => {
      if (actionResult.success) {
        this.reloadChangeRequests();
        if (actionResult.changes) {
          this.editForm.patchValue(actionResult.changes);
        }
      }
    });
  }

  reloadChangeRequests(): void {
    this.changeRequestApi.findByEntity('BeneficiaryGroup', this.beneficiaryGroup.uuid).subscribe(result => {
      this.changeRequestDataSource = result;
      this.openChangeRequestCount = this.changeRequestDataSource.filter(cr => cr.status === 'REQUESTED').length;
    });
  }

  private getLatestQuest(): Quest | null {
    let latestQuest: Quest | null = null;
    if (this.beneficiaryGroup.questList && this.beneficiaryGroup.questList.length !== 0) {
      latestQuest = this.beneficiaryGroup.questList[this.beneficiaryGroup.questList.length - 1];
    }

    return latestQuest;
  }
}
